// Native TEAM service-visit monitor (Android Capacitor shell).
// Implements the window.TFSServiceMonitor bridge expected by TeamService.jsx:
//   prepare()         -> request foreground location + notification permission,
//                        return a single high-accuracy arrival fix.
//   beginMonitoring() -> start a foreground-service background watcher that
//                        forwards GPS evidence to the secured backend.
// Evidence is buffered in a durable queue (native Preferences) and posted with
// native HTTP (CapacitorHttp) so it survives connectivity loss / process death
// and is never dropped silently. Each capture carries its ORIGINAL timestamp and
// a unique event_id for server-side de-duplication (idempotent, duplicate-safe).
import { Capacitor, CapacitorHttp, registerPlugin } from '@capacitor/core';
import { Geolocation } from '@capacitor/geolocation';
import { LocalNotifications } from '@capacitor/local-notifications';
import { Preferences } from '@capacitor/preferences';

const BackgroundGeolocation = registerPlugin('BackgroundGeolocation');
const API = process.env.REACT_APP_BACKEND_URL;
const QUEUE_KEY = 'tfs_visit_evidence_queue_v1';
const ACTIVE_KEY = 'tfs_active_visit_v1';

let watcherId = null;
let flushing = false;

const isNative = () => {
  try { return Capacitor && Capacitor.isNativePlatform && Capacitor.isNativePlatform() === true; }
  catch (_) { return false; }
};
const uuid = () => (globalThis.crypto && globalThis.crypto.randomUUID)
  ? globalThis.crypto.randomUUID()
  : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

async function readQueue() {
  const { value } = await Preferences.get({ key: QUEUE_KEY });
  try { return value ? JSON.parse(value) : []; } catch (_) { return []; }
}
async function writeQueue(q) {
  // Bounded: Preferences is lightweight key/value storage, not a database.
  await Preferences.set({ key: QUEUE_KEY, value: JSON.stringify(q.slice(-500)) });
}
async function getActive() {
  const { value } = await Preferences.get({ key: ACTIVE_KEY });
  try { return value ? JSON.parse(value) : null; } catch (_) { return null; }
}
async function setActive(a) {
  if (a) await Preferences.set({ key: ACTIVE_KEY, value: JSON.stringify(a) });
  else await Preferences.remove({ key: ACTIVE_KEY });
}

async function enqueue(evt) {
  const q = await readQueue();
  q.push(evt);
  await writeQueue(q); // persist BEFORE any network attempt (survives process death)
}

// Drains the queue chronologically (oldest first). Retains items on transient
// failures so nothing is lost; drops items the server has definitively handled.
async function flush() {
  if (flushing) return;
  flushing = true;
  try {
    let q = await readQueue();
    while (q.length) {
      const item = q[0];
      let res;
      try {
        res = await CapacitorHttp.post({
          url: `${API}/api/team/service/visits/${encodeURIComponent(item.visitId)}/position`,
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${item.token}` },
          data: {
            latitude: item.latitude, longitude: item.longitude, accuracy_m: item.accuracy_m,
            event_id: item.event_id, captured_at: item.captured_at,
          },
          connectTimeout: 10000, readTimeout: 15000,
        });
      } catch (_) {
        break; // network loss: keep the whole queue, retry on next fix / resume
      }
      const status = res.status;
      if (status >= 200 && status < 300) {
        q = q.slice(1); await writeQueue(q);
        if (res.data && res.data.active === false) { await stopMonitoring(); break; }
      } else if (status === 409) {
        // Stale/superseded evidence — safe to drop this single item and continue.
        q = q.slice(1); await writeQueue(q);
      } else if (status === 404 || status === 403) {
        // Visit gone or access revoked: stop monitoring, keep remaining evidence.
        await stopMonitoring(); break;
      } else {
        // 401 (auth expired) or 5xx: retain evidence; server flags unconfirmed visits.
        break;
      }
    }
  } finally {
    flushing = false;
  }
}

async function onLocation(location) {
  if (!location) return;
  const active = await getActive();
  if (!active || !active.visitId) return;
  const acc = typeof location.accuracy === 'number' ? location.accuracy : 9999;
  await enqueue({
    visitId: active.visitId,
    token: active.token,
    event_id: uuid(),
    captured_at: new Date(location.time || Date.now()).toISOString(),
    latitude: location.latitude,
    longitude: location.longitude,
    accuracy_m: acc,
  });
  await flush();
}

async function addWatcher() {
  if (watcherId) return;
  watcherId = await BackgroundGeolocation.addWatcher(
    {
      requestPermissions: true,   // prompts location (incl. background) on first start
      stale: false,
      distanceFilter: 25,
      backgroundTitle: 'TEAM — service visit active',
      backgroundMessage: 'Location is shared only while your service visit is active.',
    },
    (location, error) => { if (error) { return; } onLocation(location); },
  );
}

export async function stopMonitoring() {
  try { if (watcherId) await BackgroundGeolocation.removeWatcher({ id: watcherId }); }
  catch (_) { /* best effort */ }
  watcherId = null;
  await setActive(null);
}

const monitor = {
  // Foreground preparation + one-time high-accuracy arrival fix.
  async prepare() {
    try { await Geolocation.requestPermissions(); } catch (_) { /* surfaced below if fix fails */ }
    try {
      const n = await LocalNotifications.checkPermissions();
      if (n.display !== 'granted') await LocalNotifications.requestPermissions();
    } catch (_) { /* notification prompt best effort */ }
    const pos = await Geolocation.getCurrentPosition({ enableHighAccuracy: true, timeout: 20000, maximumAge: 0 });
    return { latitude: pos.coords.latitude, longitude: pos.coords.longitude, accuracy_m: pos.coords.accuracy };
  },
  // Begin durable background monitoring for an active visit.
  async beginMonitoring({ visitId, token }) {
    await setActive({ visitId, token });
    await addWatcher();
    await flush();
    return true;
  },
  stopMonitoring,
  // Re-attach the watcher after a WebView reload while a visit is still active.
  async resume(token) {
    const active = await getActive();
    if (active && active.visitId) {
      if (token) { active.token = token; await setActive(active); }
      await addWatcher();
      await flush();
    }
  },
};

if (typeof window !== 'undefined' && isNative()) {
  window.TFSServiceMonitor = monitor;
}

export default monitor;
